/**
 * Scrubbing for anything that leaves the browser in an auth error report.
 *
 * ORDERING CONTRACT — read before changing anything here:
 * normalizeCognitoError() matches on message SUBSTRINGS ('temporary password has expired',
 * 'session is expired', 'pattern: [0-9]', 'Invalid code received for user'). So the raw message
 * must be normalised FIRST and sanitised SECOND. Sanitising first would rewrite the very text
 * the classifier depends on — e.g. 'Invalid code received for user 123456' becomes
 * '... user [digits]'. There is a test asserting this ordering; it exists because this is
 * exactly the kind of thing a well-meaning refactor breaks silently.
 */

const MAX_MESSAGE = 300;

/**
 * Removes credential-shaped content and any address that identifies a person.
 *
 * The 6+ digit rule kills OTP/TOTP values that Cognito sometimes echoes back. Emails are
 * redacted here because the message is free text; the actual email travels as its own
 * validated field, where the server can mask and hash it properly.
 */
export function sanitizeMessage(raw: unknown): string {
  if (typeof raw !== 'string' || raw.length === 0) {
    return '';
  }
  return raw
    .replace(/eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g, '[jwt]')
    .replace(/Bearer\s+\S+/gi, 'Bearer [redacted]')
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '[uuid]')
    .replace(/\b\d{6,}\b/g, '[digits]')
    .slice(0, MAX_MESSAGE);
}

/**
 * Pulls ONLY name/message/status off an error.
 *
 * This function is the reason the reporter cannot leak a credential. Axios errors carry
 * `config.data` (the entire request body) and `config.url` — and AuthApiService posts to
 * /auth/exchange/{code}, so the OAuth authorization code sits in that url. Never spread an
 * error object; never read `config`, `request`, or `response.data`.
 */
export function extractErrorFacts(error: unknown): {
  name: string;
  message: string;
  httpStatus: number | null;
} {
  if (typeof error !== 'object' || error === null) {
    return { name: 'UnknownError', message: '', httpStatus: null };
  }
  const err = error as {
    name?: unknown;
    message?: unknown;
    response?: { status?: unknown };
  };
  const status = err.response?.status;
  return {
    name: typeof err.name === 'string' && err.name ? err.name : 'UnknownError',
    message: typeof err.message === 'string' ? err.message : '',
    httpStatus: typeof status === 'number' ? status : null,
  };
}
